/**
 * Navigation - canonical product information architecture.
 *
 * Every item is a real, wired, working route. Projects are first-class and live
 * at the very top of the sidebar (rendered by SidebarProjects), above these
 * groups. Structure reinforces Project -> Build -> Deploy -> Operate:
 *   BUILD       → the launcher
 *   SHIP        → deploy and distribute
 *   OPERATE     → operational health + ecosystem
 *   ACCOUNT     → billing and settings
 *   HELP       → documentation, product updates, and customer support
 *
 * Integrations are project-scoped (workspace > Integrations), so there is no
 * global Connectors entry here.
 */

import {
  LayoutDashboard,
  FolderKanban,
  Rocket,
  Smartphone,
  Store,
  TrendingUp,
  CreditCard,
  Settings,
  LifeBuoy,
  FileText,
  Puzzle,
  Plug2,
  Cpu,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
  /** English label, kept as a stable default; the UI renders titleKey via i18n. */
  title:    string;
  /** i18n catalog key for the localized label (see src/i18n/catalogs). */
  titleKey: string;
  url:      string;
  icon:     LucideIcon;
}

export interface NavGroup {
  label:    string;
  labelKey: string;
  items:    NavItem[];
}

export const navigationGroups: NavGroup[] = [
  {
    label: "Build", labelKey: "nav.group.build",
    items: [
      { title: "Dashboard", titleKey: "nav.item.dashboard", url: "/dashboard", icon: LayoutDashboard },
      { title: "Projects",  titleKey: "nav.item.projects",  url: "/projects",  icon: FolderKanban    },
      { title: "Documents", titleKey: "nav.item.documents", url: "/documents", icon: FileText        },
      { title: "Creative Studio", titleKey: "nav.item.creativeStudio", url: "/creative-studio", icon: Sparkles },
      { title: "Library", titleKey: "nav.item.library", url: "/library", icon: FolderKanban },
    ],
  },
  {
    label: "Ship", labelKey: "nav.group.ship",
    items: [
      { title: "Deployments",  titleKey: "nav.item.deployments",  url: "/deploy", icon: Rocket     },
      { title: "Mobile Builds", titleKey: "nav.item.mobileBuilds", url: "/mobile", icon: Smartphone },
    ],
  },
  {
    label: "Operate", labelKey: "nav.group.operate",
    items: [
      // NO "System Status" HERE. It pointed at /platform, the operator console,
      // which every customer was shown in the nav and none of them could open:
      // the console is admin-only, so a customer who tapped it was bounced
      // straight back to the dashboard. A door that opens for nobody is worse
      // than no door, because the customer reads the rejection as a fault in
      // the product rather than a boundary in it.
      //
      // The label was wrong too, and that is the deeper half. "System Status"
      // promises a fair question ("is Nuvra up right now?") that this codebase
      // genuinely answers, and it never pointed at the answer. Even an admin
      // tapping it landed on release pipelines and audit logs.
      //
      // The real answer keeps three surfaces, none of them removed: the live
      // status dot in the sidebar footer (now a link to the full panel), the
      // Settings > System Status panel (real service health from /api/status,
      // no fabricated uptime), and the incident banner. The operator console
      // keeps its own admin-gated door in the account menu.
      // "Engineering", not "Engineering Intelligence": the full name was 24 characters,
      // twice the next longest label in this sidebar, and it crowded the nav on a phone.
      // The page keeps its full heading; only the nav label is short.
      { title: "Engineering",   titleKey: "nav.item.engineering",   url: "/engineering", icon: Cpu },
      { title: "Marketplace",    titleKey: "nav.item.marketplace",   url: "/marketplace", icon: Store      },
      { title: "Plugins",        titleKey: "nav.item.plugins",       url: "/extensions",    icon: Puzzle     },
      { title: "Integrations",   titleKey: "nav.item.integrations",  url: "/integrations",  icon: Plug2      },
      { title: "Creator Studio", titleKey: "nav.item.creatorStudio", url: "/creator",     icon: TrendingUp },
    ],
  },
  {
    label: "Account", labelKey: "nav.group.account",
    items: [
      { title: "Billing",  titleKey: "nav.item.billing",  url: "/billing",  icon: CreditCard },
      { title: "Settings", titleKey: "nav.item.settings", url: "/settings", icon: Settings   },
    ],
  },
  {
    label: "Help", labelKey: "nav.group.help",
    items: [
      // Help is the single customer-facing support destination. Documentation, product
      // updates, and the full support conversation flow are reachable from the Help Center,
      // keeping the primary sidebar focused on the work customers came here to do.
      // What's New remains a stable deep link for notifications, emails, bookmarks, and
      // contextual links; it is intentionally not a top-level sidebar destination.
      { title: "Help Center", titleKey: "nav.item.helpCenter", url: "/help", icon: LifeBuoy },
    ],
  },
];

// Legacy exports - kept so callers don't break during migration.
export type NavigationContext = "global";
export function detectContext(_pathname: string): NavigationContext { return "global"; }
export function getNavigationGroups(_ctx: NavigationContext): NavGroup[] { return navigationGroups; }
export function getAllNavigationGroups(): NavGroup[] { return navigationGroups; }
